import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ContextMenu from '../components/ContextMenu';

const Grievances = () => {
  const [grievances, setGrievances] = useState([]);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [stats, setStats] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingGrievance, setEditingGrievance] = useState(null);
  const [formData, setFormData] = useState({
    type: '',
    studentId: '',
    tutorId: '',
    description: '',
    status: 'Open',
    priority: 'Medium',
    resolution: ''
  });
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    priority: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchGrievances();
    fetchStudents();
    fetchTutors();
    fetchStats();
  }, [filters]);

  const fetchGrievances = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);

      const response = await axios.get(`/api/grievances?${params.toString()}`);
      setGrievances(response.data.data || []);
    } catch (error) {
      console.error('Error fetching grievances:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get('/api/student-finals?limit=1000');
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchTutors = async () => {
    try {
      const response = await axios.get('/api/tutor-finals?limit=1000');
      setTutors(response.data.tutors || []);
    } catch (error) {
      console.error('Error fetching tutors:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/grievances/stats/summary');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (editingGrievance) {
        await axios.put(`/api/grievances/${editingGrievance._id}`, formData);
        setMessage('Grievance updated successfully!');
      } else {
        await axios.post('/api/grievances', formData);
        setMessage('Grievance created successfully!');
      }

      resetForm();
      fetchGrievances();
      fetchStats();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving grievance:', error);
      setMessage(error.response?.data?.message || 'Error saving grievance');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (grievance) => {
    setEditingGrievance(grievance);
    setFormData({
      type: grievance.type || '',
      studentId: grievance.studentId?._id || '',
      tutorId: grievance.tutorId?._id || '',
      description: grievance.description || '',
      status: grievance.status || 'Open',
      priority: grievance.priority || 'Medium',
      resolution: grievance.resolution || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this grievance?')) return;

    try {
      await axios.delete(`/api/grievances/${id}`);
      setMessage('Grievance deleted successfully!');
      fetchGrievances();
      fetchStats();
    } catch (error) {
      console.error('Error deleting grievance:', error);
      setMessage('Error deleting grievance');
    }
  };

  const resetForm = () => {
    setFormData({
      type: '',
      studentId: '',
      tutorId: '',
      description: '',
      status: 'Open',
      priority: 'Medium',
      resolution: ''
    });
    setEditingGrievance(null);
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      status: '',
      priority: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'status-created';
      case 'In Progress': return 'status-assigned';
      case 'Resolved': return 'status-delivered';
      case 'Closed': return 'status-inactive';
      default: return 'status-created';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return '#dc2626';
      case 'High': return '#f59e0b';
      case 'Medium': return '#3b82f6';
      case 'Low': return '#10b981';
      default: return '#6b7280';
    }
  };

  return (
    <div className="container" style={{ padding: '32px 24px' }}>
      <div className="page-header">
        <h1>Grievances / Tickets Management</h1>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          + Create New Ticket
        </button>
      </div>

      {message && (
        <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}
        </div>
      )}

      {/* Statistics */}
      {stats && (
        <div className="grid">
          <div className="card">
            <h3>Total Tickets</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>{stats.total}</p>
          </div>
          <div className="card">
            <h3>Open</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>{stats.open}</p>
          </div>
          <div className="card">
            <h3>In Progress</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>{stats.inProgress}</p>
          </div>
          <div className="card">
            <h3>Resolved</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>{stats.resolved}</p>
          </div>
          <div className="card">
            <h3>Closed</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#6b7280' }}>{stats.closed}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <h3>Filters</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div className="form-group">
            <label>Type</label>
            <select name="type" value={filters.type} onChange={handleFilterChange}>
              <option value="">All Types</option>
              <option value="Teacher Change Request">Teacher Change Request</option>
              <option value="Teacher Complaint">Teacher Complaint</option>
              <option value="Tutor Payment Issue">Tutor Payment Issue</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Status</label>
            <select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="">All Statuses</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          <div className="form-group">
            <label>Priority</label>
            <select name="priority" value={filters.priority} onChange={handleFilterChange}>
              <option value="">All Priorities</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={clearFilters} style={{ width: '100%' }}>
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Grievances List ({grievances.length})</h3>
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Type</th>
                  <th>Student</th>
                  <th>Tutor</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {grievances.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>No grievances found</td>
                  </tr>
                ) : (
                  grievances.map((grievance) => (
                    <tr key={grievance._id}>
                      <td>
                        <strong style={{ color: '#3b82f6' }}>{grievance.ticketNumber}</strong>
                      </td>
                      <td>{grievance.type}</td>
                      <td>{grievance.studentId?.name || '-'}</td>
                      <td>{grievance.tutorId?.name || '-'}</td>
                      <td>
                        <span style={{
                          color: getPriorityColor(grievance.priority),
                          fontWeight: '600'
                        }}>
                          {grievance.priority}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusColor(grievance.status)}`}>
                          {grievance.status}
                        </span>
                      </td>
                      <td>{new Date(grievance.createdAt).toLocaleDateString()}</td>
                      <td className="actions">
                        <ContextMenu
                          onEdit={() => handleEdit(grievance)}
                          onDelete={() => handleDelete(grievance._id)}
                          itemName={`Ticket ${grievance.ticketNumber}`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <h2>{editingGrievance ? `Edit Ticket ${editingGrievance.ticketNumber}` : 'Create New Ticket'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Type *</label>
                  <select name="type" value={formData.type} onChange={handleChange} required>
                    <option value="">Select Type</option>
                    <option value="Teacher Change Request">Teacher Change Request</option>
                    <option value="Teacher Complaint">Teacher Complaint</option>
                    <option value="Tutor Payment Issue">Tutor Payment Issue</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority *</label>
                  <select name="priority" value={formData.priority} onChange={handleChange} required>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Student</label>
                  <select name="studentId" value={formData.studentId} onChange={handleChange}>
                    <option value="">Select Student (Optional)</option>
                    {students.map((student) => (
                      <option key={student._id} value={student._id}>
                        {student.name} - {student.class}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tutor</label>
                  <select name="tutorId" value={formData.tutorId} onChange={handleChange}>
                    <option value="">Select Tutor (Optional)</option>
                    {tutors.map((tutor) => (
                      <option key={tutor._id} value={tutor._id}>
                        {tutor.name}
                      </option>
                    ))}
                  </select>
                </div>
                {editingGrievance && (
                  <div className="form-group">
                    <label>Status *</label>
                    <select name="status" value={formData.status} onChange={handleChange} required>
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Resolved">Resolved</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Describe the issue in detail"
                  required
                ></textarea>
              </div>

              {(editingGrievance && (formData.status === 'Resolved' || formData.status === 'Closed')) && (
                <div className="form-group">
                  <label>Resolution</label>
                  <textarea
                    name="resolution"
                    value={formData.resolution}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe how the issue was resolved"
                  ></textarea>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Grievances;
